/**
 * Tracks full-address values that were selected from the Google Maps
 * autocomplete dropdown so validation can reject free-typed (unstructured)
 * addresses. Only fullAddress selections may be recorded here — partial
 * values (street/city/postal) must never enter the set, or they would pass
 * fullAddress validation.
 *
 * Values are kept in a module-level set: any full address ever picked from
 * the dropdown counts as a valid selection. This keeps validators (which only
 * receive the field value) able to distinguish selected from typed input, and
 * behaves correctly with multiple forms per page and across SPA pages (a
 * per-field "last selected value" would let forms sharing the fullAddress
 * field name invalidate each other).
 */

const selectedAddresses = new Set<string>();

/**
 * Field names for which the autocomplete dropdown is actually functional
 * (i.e. the Maps API key loaded). Strict validation must only apply when the
 * user is able to select a suggestion — otherwise a Maps outage would make
 * the form impossible to submit.
 */
const operationalFields = new Set<string>();

export const recordSelectedAddress = (value: string): void => {
    if (value) {
        selectedAddresses.add(value);
    }
};

export const isSelectedAddress = (value: string): boolean => {
    return selectedAddresses.has(value);
};

export const markAutocompleteOperational = (fieldName: string): void => {
    operationalFields.add(fieldName);
};

export const isAutocompleteOperational = (fieldName: string): boolean => {
    return operationalFields.has(fieldName);
};

/**
 * Test-only helper to reset module state between tests.
 */
export const resetAutocompleteSelectionState = (): void => {
    selectedAddresses.clear();
    operationalFields.clear();
};
